import { useCallback, useEffect, useRef, useState } from 'react'
import { subscribe } from 'valtio'
import { toast } from 'sonner'
import { Wifi, WifiOff } from 'lucide-react'
import { TodoApp } from '@/components/todo-app'
import { TermsOfUse } from '@/components/terms-of-use'
import { PrivacyPolicy } from '@/components/privacy-policy'
import { Toaster } from '@/components/ui/sonner'
import { buildBackendUrl } from '@/lib/backend-url'
import { useSafePosthog } from './post'
import { useFCM } from './hooks/use-fcm'
import { useReminderNotifications } from './hooks/use-reminder-notifications'
import { store } from '@/store'
import {
  GuestValuePrompt,
  ProDialog,
  ProValuePrompt,
  SignInDialog,
} from '@/components/auth-growth-prompts'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID
const ONE_TAP_SCRIPT_ID = 'google-one-tap-script'
const GROWTH_STORAGE_KEY = 'zoy_growth_funnel_v1'
const PROMPT_COOLDOWN_MS = 1000 * 60 * 20

const loadGoogleScript = () =>
  new Promise((resolve, reject) => {
    const existing = document.getElementById(ONE_TAP_SCRIPT_ID)
    if (existing) {
      resolve()
      return
    }

    const script = document.createElement('script')
    script.id = ONE_TAP_SCRIPT_ID
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Failed to load Google One Tap script'))
    document.head.appendChild(script)
  })

const createGrowthState = () => ({
  version: 1,
  firstSeenAt: Date.now(),
  totalActions: 0,
  aiActions: 0,
  tasksCreated: 0,
  listsCreated: 0,
  tasksCompleted: 0,
  tasksEnhanced: 0,
  loginPromptSeenCount: 0,
  upgradePromptSeenCount: 0,
  loginPromptDismissedAt: 0,
  upgradePromptDismissedAt: 0,
  lastMeaningfulActionAt: 0,
})

const readStoredAuthUser = () => {
  try {
    const raw = localStorage.getItem('zoy_auth_user')
    return raw ? JSON.parse(raw) : null
  } catch (error) {
    console.error('Failed to parse auth user', error)
    return null
  }
}

const readGrowthState = () => {
  try {
    const raw = localStorage.getItem(GROWTH_STORAGE_KEY)
    if (!raw) return createGrowthState()
    return { ...createGrowthState(), ...JSON.parse(raw) }
  } catch (error) {
    console.error('Failed to parse growth state', error)
    return createGrowthState()
  }
}

const persistGrowthState = (nextState) => {
  localStorage.setItem(GROWTH_STORAGE_KEY, JSON.stringify(nextState))
}

const getUsageSnapshot = () => {
  const todos = store.todos || []
  const lists = store.lists || []

  let completedCount = 0
  let enhancedCount = 0

  for (const todo of todos) {
    if (todo.status === 'done' || todo.completed) {
      completedCount += 1
    }

    const hasEnhancement = Boolean(
      todo.description ||
      todo.endDate ||
      todo.reminders?.length ||
      todo.locations?.length ||
      todo.assignees?.length ||
      todo.attachments?.length ||
      todo.comments?.length ||
      todo.subtasks?.length,
    )

    if (hasEnhancement) {
      enhancedCount += 1
    }
  }

  return {
    todoCount: todos.length,
    listCount: lists.length,
    completedCount,
    enhancedCount,
    aiActions: store.aiActions || 0,
  }
}

const computeGrowthDelta = (previousSnapshot, nextSnapshot) => {
  const taskCreates = Math.max(0, nextSnapshot.todoCount - previousSnapshot.todoCount)
  const listCreates = Math.max(0, nextSnapshot.listCount - previousSnapshot.listCount)
  const tasksCompleted = Math.max(0, nextSnapshot.completedCount - previousSnapshot.completedCount)
  const tasksEnhanced = Math.max(0, nextSnapshot.enhancedCount - previousSnapshot.enhancedCount)
  const aiActions = Math.max(0, (nextSnapshot.aiActions || 0) - (previousSnapshot.aiActions || 0))
  const totalActions = taskCreates + listCreates + tasksCompleted + tasksEnhanced

  return {
    taskCreates,
    listCreates,
    tasksCompleted,
    tasksEnhanced,
    aiActions,
    totalActions,
  }
}

const isCooldownExpired = (timestamp, aggressive = false) => {
  const cooldown = aggressive ? PROMPT_COOLDOWN_MS / 4 : PROMPT_COOLDOWN_MS
  return !timestamp || Date.now() - timestamp > cooldown
}

const shouldShowLoginPrompt = (growthState) =>
  growthState.totalActions >= 3 &&
  (growthState.tasksCreated >= 1 ||
    growthState.tasksEnhanced >= 1 ||
    growthState.tasksCompleted >= 1)

const shouldShowUpgradePrompt = (growthState) =>
  growthState.aiActions >= 33 && growthState.totalActions >= 10 && growthState.tasksCreated >= 3

export function App() {
  const { diagnostics } = useSafePosthog()
  const [isLoggedIn, setIsLoggedIn] = useState(() => !!localStorage.getItem('zoy_auth_token'))
  const [authUser, setAuthUser] = useState(() => readStoredAuthUser())
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [growthState, setGrowthState] = useState(() => readGrowthState())
  const [isSignInDialogOpen, setIsSignInDialogOpen] = useState(false)
  const [isGuestPromptVisible, setIsGuestPromptVisible] = useState(false)
  const [isUpgradePromptVisible, setIsUpgradePromptVisible] = useState(false)
  const [isProDialogOpen, setIsProDialogOpen] = useState(false)
  const [currentRoute, setCurrentRoute] = useState(() => window.location.pathname)
  const usageSnapshotRef = useRef(getUsageSnapshot())
  const handleCredentialRef = useRef(null)
  const userId = authUser?.id

  useFCM(userId)
  useReminderNotifications(userId)

  useEffect(() => {
    const handlePopState = () => {
      setCurrentRoute(window.location.pathname)
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const navigate = useCallback((path) => {
    window.history.pushState({}, '', path)
    setCurrentRoute(path)
  }, [])

  const dismissGuestPrompt = useCallback(() => {
    setIsGuestPromptVisible(false)
    setGrowthState((currentState) => {
      const nextState = {
        ...currentState,
        loginPromptDismissedAt: Date.now(),
      }
      persistGrowthState(nextState)
      return nextState
    })
  }, [])

  const dismissUpgradePrompt = useCallback(() => {
    setIsUpgradePromptVisible(false)
    setGrowthState((currentState) => {
      const nextState = {
        ...currentState,
        upgradePromptDismissedAt: Date.now(),
      }
      persistGrowthState(nextState)
      return nextState
    })
  }, [])

  const openSignInDialog = useCallback(() => {
    setIsGuestPromptVisible(false)
    setIsSignInDialogOpen(true)
  }, [])

  const handleContinueFree = useCallback(() => {
    setIsSignInDialogOpen(false)
    dismissGuestPrompt()
  }, [dismissGuestPrompt])

  const handleUpgradeClick = useCallback(() => {
    setIsUpgradePromptVisible(false)
    setIsProDialogOpen(true)
  }, [])

  const handleUpgradeConfirm = useCallback(() => {
    window.record('pro_upgrade_clicked', {
      total_actions: growthState.totalActions,
      tasks_created: growthState.tasksCreated,
    })
    toast('Pro checkout is not configured yet in this build.', {
      description:
        'The upgrade prompt is live. Billing still needs to be wired to a real checkout.',
    })
    dismissUpgradePrompt()
    setIsProDialogOpen(false)
  }, [dismissUpgradePrompt, growthState.tasksCreated, growthState.totalActions])

  useEffect(() => {
    const unsubscribe = subscribe(store, () => {
      const nextSnapshot = getUsageSnapshot()
      const delta = computeGrowthDelta(usageSnapshotRef.current, nextSnapshot)
      usageSnapshotRef.current = nextSnapshot

      if (delta.totalActions === 0) {
        return
      }

      setGrowthState((currentState) => {
        const nextState = {
          ...currentState,
          totalActions: currentState.totalActions + delta.totalActions,
          aiActions: currentState.aiActions + delta.aiActions,
          tasksCreated: currentState.tasksCreated + delta.taskCreates,
          listsCreated: currentState.listsCreated + delta.listCreates,
          tasksCompleted: currentState.tasksCompleted + delta.tasksCompleted,
          tasksEnhanced: currentState.tasksEnhanced + delta.tasksEnhanced,
          lastMeaningfulActionAt: Date.now(),
        }

        persistGrowthState(nextState)
        return nextState
      })
    })

    return unsubscribe
  }, [])

  useEffect(() => {
    const syncSession = () => {
      setIsLoggedIn(!!localStorage.getItem('zoy_auth_token'))
      setAuthUser(readStoredAuthUser())
    }

    window.addEventListener('storage', syncSession)
    window.addEventListener('focus', syncSession)

    return () => {
      window.removeEventListener('storage', syncSession)
      window.removeEventListener('focus', syncSession)
    }
  }, [])

  useEffect(() => {
    if (!isLoggedIn) return

    const fetchLatestUser = async () => {
      const token = localStorage.getItem('zoy_auth_token')
      if (!token) return

      try {
        const response = await fetch(buildBackendUrl('/api/me'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          if (data.user) {
            localStorage.setItem('zoy_auth_user', JSON.stringify(data.user))
            setAuthUser(data.user)
          }
        }
      } catch (err) {
        console.error('[auth] Failed to fetch latest user profile', err)
      }
    }

    fetchLatestUser()
  }, [isLoggedIn])

  useEffect(() => {
    setTimeout(() => {
      if (diagnostics.isBlocked() && import.meta.env.PROD) {
        console.warn('[telemetry] PostHog degraded', {
          ready: diagnostics.isReady(),
          network: diagnostics.hasNetworkActivity(),
        })
      }
    }, 4000)
  }, [diagnostics])

  useEffect(() => {
    window.record('app_loaded')

    const handleOnline = () => {
      toast.success('You are back online', {
        icon: <Wifi className="h-4 w-4 text-green-500" />,
      })
    }
    const handleOffline = () => {
      toast.error('You are offline', {
        description: "Don't worry, the app still works offline.",
        icon: <WifiOff className="h-4 w-4" />,
      })
    }
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    if (isLoggedIn) {
      setIsGuestPromptVisible(false)
      setIsSignInDialogOpen(false)
      return
    }

    if (
      shouldShowLoginPrompt(growthState) &&
      isCooldownExpired(growthState.loginPromptDismissedAt) &&
      !isSignInDialogOpen &&
      !isGuestPromptVisible
    ) {
      setIsGuestPromptVisible(true)
      setGrowthState((currentState) => {
        const nextState = {
          ...currentState,
          loginPromptSeenCount: currentState.loginPromptSeenCount + 1,
        }
        persistGrowthState(nextState)
        return nextState
      })
    }
  }, [growthState, isGuestPromptVisible, isLoggedIn, isSignInDialogOpen])

  useEffect(() => {
    if (!isLoggedIn) {
      setIsUpgradePromptVisible(false)
      setIsProDialogOpen(false)
      return
    }

    if (
      shouldShowUpgradePrompt(growthState) &&
      isCooldownExpired(growthState.upgradePromptDismissedAt, growthState.totalActions > 30) &&
      !isProDialogOpen &&
      !isUpgradePromptVisible
    ) {
      setIsUpgradePromptVisible(true)
      setGrowthState((currentState) => {
        const nextState = {
          ...currentState,
          upgradePromptSeenCount: currentState.upgradePromptSeenCount + 1,
        }
        persistGrowthState(nextState)
        return nextState
      })
    }
  }, [growthState, isLoggedIn, isProDialogOpen, isUpgradePromptVisible])

  const handleSignInDialogOpenChange = useCallback(
    (open) => {
      setIsSignInDialogOpen(open)
      if (!open) {
        dismissGuestPrompt()
      }
    },
    [dismissGuestPrompt],
  )

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      console.warn('VITE_GOOGLE_CLIENT_ID is missing, skipping Google One Tap setup')
      return
    }
    if (localStorage.getItem('zoy_auth_token')) {
      return
    }

    let cancelled = false

    const handleCredential = async (response) => {
      if (cancelled || !response?.credential) {
        return
      }

      setIsAuthenticating(true)

      try {
        const authRes = await fetch(buildBackendUrl('/api/auth/one-tap/callback'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idToken: response.credential }),
        })

        if (!authRes.ok) {
          const errData = await authRes.json().catch(() => ({}))
          throw new Error(errData.error || 'One Tap auth failed')
        }

        const authData = await authRes.json()
        const bootstrapRes = await fetch(buildBackendUrl('/api/bootstrap'), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authData.token}`,
          },
        })

        if (!bootstrapRes.ok) {
          throw new Error('Account bootstrap failed')
        }

        const bootstrapData = await bootstrapRes.json()

        localStorage.setItem('zoy_auth_token', authData.token)
        localStorage.setItem('zoy_auth_user', JSON.stringify(authData.user))
        localStorage.setItem('zoy_project_id', bootstrapData.projectId)
        localStorage.setItem('zoy_workspace_id', bootstrapData.workspaceId)

        setIsLoggedIn(true)
        setAuthUser(authData.user)
        setIsAuthenticating(false)
        setIsSignInDialogOpen(false)

        window.record('signup_completed', {
          total_actions: growthState.totalActions,
          tasks_created: growthState.tasksCreated,
        })

        toast.success(`Saved. Welcome ${authData.user?.name || ''}`.trim(), {
          description: 'Your workspace can now sync across devices.',
        })
        window.location.reload()
      } catch (error) {
        console.error('[auth] Google One Tap sign-in failed', error)
        setIsAuthenticating(false)
        localStorage.removeItem('zoy_auth_token')
        localStorage.removeItem('zoy_auth_user')
        localStorage.removeItem('zoy_project_id')
        localStorage.removeItem('zoy_workspace_id')
        toast.error(error instanceof Error ? error.message : 'Sign-in failed. Please try again.')
      }
    }

    handleCredentialRef.current = handleCredential

    const setupOneTap = async () => {
      try {
        await loadGoogleScript()

        let attempts = 0
        while (!window.google?.accounts?.id && attempts < 60) {
          if (cancelled) return
          await new Promise((resolve) => setTimeout(resolve, 100))
          attempts++
        }

        if (cancelled || !window.google?.accounts?.id) {
          return
        }

        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => handleCredentialRef.current?.(response),
          auto_select: true,
          context: 'signin',
          ux_mode: 'popup',
          itp_support: true,
          use_fedcm_for_prompt: false,
        })

        const buttonParent = document.getElementById('google-signin-button')
        if (buttonParent) {
          buttonParent.innerHTML = ''
          window.google.accounts.id.renderButton(buttonParent, {
            theme: 'outline',
            size: 'large',
            shape: 'pill',
            width: 280,
          })
        }

        if (!shouldShowLoginPrompt(growthState)) {
          window.google.accounts.id.prompt()
        }
      } catch (error) {
        console.error('Failed to initialize Google One Tap', error)
      }
    }

    setupOneTap()
    return () => {
      cancelled = true
    }
  }, [growthState.tasksCreated, growthState.totalActions])

  useEffect(() => {
    if (!isSignInDialogOpen || !window.google?.accounts?.id) {
      return
    }

    const buttonParent = document.getElementById('google-signin-button')
    if (!buttonParent) {
      return
    }

    buttonParent.innerHTML = ''
    window.google.accounts.id.renderButton(buttonParent, {
      theme: 'outline',
      size: 'large',
      shape: 'pill',
      width: 280,
    })
  }, [isSignInDialogOpen])

  const renderContent = () => {
    if (currentRoute === '/terms') {
      return <TermsOfUse navigate={navigate} />
    }
    if (currentRoute === '/privacy') {
      return <PrivacyPolicy navigate={navigate} />
    }
    return <TodoApp />
  }

  return (
    <div className="relative min-h-screen font-sans selection:bg-primary/10">
      {renderContent()}

      <GuestValuePrompt
        open={isGuestPromptVisible}
        onDismiss={dismissGuestPrompt}
        onSignIn={openSignInDialog}
      />

      <ProValuePrompt
        open={isUpgradePromptVisible}
        onDismiss={dismissUpgradePrompt}
        onUpgrade={handleUpgradeClick}
        growthState={growthState}
      />

      <SignInDialog
        open={isSignInDialogOpen}
        isAuthenticating={isAuthenticating}
        onOpenChange={handleSignInDialogOpenChange}
        onContinueFree={handleContinueFree}
      />

      <ProDialog
        open={isProDialogOpen}
        onOpenChange={setIsProDialogOpen}
        onUpgrade={handleUpgradeConfirm}
        onKeepFree={() => {
          setIsProDialogOpen(false)
          dismissUpgradePrompt()
        }}
      />

      <Toaster position="bottom-center" />
    </div>
  )
}

export default App
